import React, { Fragment, useEffect, useMemo } from 'react';
import { Alert, Button, Checkbox, Tooltip } from 'antd';
import TextField from 'components/form/fields/TextField';
import { useFormik } from 'formik';
import Icon from '@mdi/react';
import { mdiCheck, mdiLink } from '@mdi/js';
import Currency from 'components/Currency';
import * as yup from 'yup';
import { useRequestWoocommerceAuthorizationMutation } from 'modules/apps/apps/WoocommerceApp/api/woocommerce.api';
import { debounce } from 'lodash';
import AutomaticDrawer from 'components/Drawers/AutomaticDrawer';
import useQueryParams from 'hooks/useQueryParams';
import useWoocommerceDomainCheck from 'modules/apps/apps/WoocommerceApp/hooks/useWoocommerceDomainCheck';

const WoocommerceConnectDrawer = (props) => {
  const { ...rest } = props;
  const [requestWoocommerceStoreAuthorizationQuery, { isLoading: isConnectLoading }] =
    useRequestWoocommerceAuthorizationMutation();

  // Domain checking
  const {
    check: checkStoreUrl,
    canConnect,
    canCheckAgain,
    isLoading: isCheckLoading,
    isChecked,
    isWoocommerceInstalled,
    woocommerceInstallUrl,
    isMyDataNinjaInstalled,
    mydataninjaIntallUrl,
  } = useWoocommerceDomainCheck();

  // Create woocommerce form, with default search params
  const { searchParams } = useQueryParams();

  const onFormSubmit = () => {
    if (!canConnect) return alert('Please fill form');
    requestWoocommerceStoreAuthorizationQuery(form.values)
      .unwrap()
      .then((data) => {
        window.open(data.redirect_url, '_blank');
      });
  };

  const form = useFormik({
    initialValues: {
      base_url: searchParams?.base_url || '',
      name: searchParams?.name || '',
      /** @todo validate */
      currency: String(searchParams?.currency || 'USD').toUpperCase(),
      data_consent: true,
    },
    onSubmit: onFormSubmit,
    validationSchema: validationSchema,
  });

  const checkStore = () => {
    if (form.values.base_url.length) {
      checkStoreUrl(form.values.base_url);
    }
  };

  useEffect(() => {
    checkStore();
  }, [form.values.base_url]);

  return (
    <AutomaticDrawer title='Connect Woocommerce Store' {...rest}>
      <div className='flex flex-col gap-6 mt-4'>
        <TextField
          error={form.touched.name && form.errors.name}
          helperText={form.errors.name}
          label='Store Name'
          name='name'
          value={form.values.name}
          onChange={form.handleChange}
        />
        <TextField
          label='Store Url'
          name='base_url'
          value={form.values.base_url}
          error={form.touched.base_url && form.errors.base_url}
          defaultValue={form.values.base_url}
          helperText={form.errors.base_url}
          onChange={form.handleChange}
        />
        <Currency
          value={form.values.currency}
          onChange={(val) => form.setFieldValue('currency', val)}
          size='large'
          dropdownStyle={{ zIndex: 2000 }}
        />
        <Tooltip
          zIndex={999999999999}
          title='If not checked we will not sync and process customer personal information in our crm'
        >
          <Checkbox
            onChange={(e) => form.setFieldValue('data_consent', e.target.checked)}
            checked={form.values.data_consent}
          >
            Process customer personal information
          </Checkbox>
        </Tooltip>
        <div className='flex justify-center gap-2'>
          <Button
            className='flex items-center justify-center'
            onClick={form.handleSubmit}
            loading={isCheckLoading || isConnectLoading}
            type='primary'
            icon={<Icon path={mdiLink} />}
            disabled={!canConnect}
          >
            {isCheckLoading ? 'Checking Your Website' : 'Connect'}
          </Button>
          {canCheckAgain && (
            <Button
              className='flex items-center justify-center'
              onClick={checkStore}
              loading={isCheckLoading || isConnectLoading}
              type='primary'
              icon={<Icon path={mdiCheck} />}
            >
              Check Again
            </Button>
          )}
        </div>
        <div className='w-full flex-col flex gap-2'>
          {isChecked && !isWoocommerceInstalled ? (
            <Alert
              type='error'
              closable={true}
              message='WooCommerce is not installed'
              description={
                <Fragment>
                  WooCommerce plugin is not installed or active in your store.
                  <a target='_blank' rel='noreferrer' href={woocommerceInstallUrl}>
                    Click here
                  </a>
                  to install it
                </Fragment>
              }
            />
          ) : null}
          {isChecked && !isMyDataNinjaInstalled ? (
            <Alert
              type='error'
              closable={true}
              message='MyDataNinja is not installed or active'
              description={
                <Fragment>
                  MyDataNinja plugin is not installed or active in your store.
                  <a target='_blank' rel='noreferrer' href={mydataninjaIntallUrl}>
                    Click here
                  </a>
                  to download and install it
                </Fragment>
              }
            />
          ) : null}
        </div>
      </div>
    </AutomaticDrawer>
  );
};

WoocommerceConnectDrawer.requiredParams = {
  connect: (param) => param === 'woocommerce',
};

const validationSchema = yup.object({
  base_url: yup.string().url('Please enter a valid URL').required('Please enter a valid URL'),
  name: yup.string('Enter name').required('Enter name').min(2).max(50),
});

export default WoocommerceConnectDrawer;
